import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Settings as SettingsIcon,
  Lock,
  Building2,
  ShieldCheck,
  Plus,
  KeyRound,
  UserPlus,
  Database,
  Download,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { authApi } from "../api/auth";
import { downloadDatabaseBackup } from "../api/admin";
import {
  useUsers,
  useCreateUser,
  useSetUserActive,
  useResetUserPassword,
  CreateUserInput,
} from "../api/users";
import { useMyClientProfile, useUpdateMyProfile } from "../api/clients";
import { Role, User } from "../types/models";
import { PageHeader } from "../components/ui/PageHeader";
import { ActiveBadge } from "../components/ui/Badge";
import { Modal } from "../components/ui/Modal";
import { Button, SelectField, TextField } from "../components/ui/Form";
import { LoadingState, ErrorState } from "../components/ui/States";
import { toast } from "../components/ui/Toast";
import { getErrorMessage } from "../lib/errors";
import { humanizeEnum } from "../utils/helpers";

const INTERNAL_ROLES: Role[] = [
  Role.ADMIN,
  Role.MANAGER,
  Role.STORE_KEEPER,
  Role.PRODUCTION_SUPERVISOR,
];

export const Settings: React.FC = () => {
  const { user, isClient, hasRole } = useAuth();
  const isAdmin = hasRole(Role.ADMIN);

  return (
    <div className="space-y-6">
      <PageHeader title="Settings" subtitle="Account, security and administration." />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <AccountCard user={user} />
        <ChangePasswordCard />
      </div>

      {isClient && <ClientProfileCard />}
      {isAdmin && <BackupSection />}
      {isAdmin && <UsersAdminSection />}
    </div>
  );
};

// ───────────────────────── Database backup ─────────────────────────

const BackupSection: React.FC = () => {
  const [downloading, setDownloading] = useState(false);

  const onBackup = async () => {
    setDownloading(true);
    try {
      await downloadDatabaseBackup();
      toast.success("Backup downloaded");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card title="Database Backup" icon={<Database size={20} className="text-cyan-400" />}>
      <p className="mb-4 max-w-2xl text-sm text-slate-400">
        Download a complete snapshot of the entire database — every table and all
        records — as a single JSON file. Keep it somewhere safe; you can use it to
        restore your data if anything goes wrong.
      </p>
      <Button icon={<Download size={16} />} loading={downloading} onClick={onBackup}>
        Download Backup
      </Button>
    </Card>
  );
};

const Card: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode; action?: React.ReactNode }> = ({ title, icon, children, action }) => (
  <div className="card p-6">
    <div className="mb-5 flex items-center justify-between">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-100">{icon} {title}</h2>
      {action}
    </div>
    {children}
  </div>
);

const AccountCard: React.FC<{ user: User | null }> = ({ user }) => (
  <Card title="Account" icon={<SettingsIcon size={20} className="text-blue-400" />}>
    <dl className="space-y-3 text-sm">
      <Row label="Name" value={user?.fullName} />
      <Row label="Email" value={user?.email} />
      <Row label="Phone" value={user?.phone ?? "—"} />
      <Row label="Role" value={humanizeEnum(user?.role)} />
    </dl>
  </Card>
);

const Row: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <div className="flex items-center justify-between border-b border-slate-700/60 pb-2 last:border-0">
    <dt className="text-slate-400">{label}</dt>
    <dd className="font-medium text-slate-200">{value}</dd>
  </div>
);

// ───────────────────────── Change password ─────────────────────────

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, "Required"),
    newPassword: z.string().min(8, "At least 8 characters"),
    confirm: z.string(),
  })
  .refine((v) => v.newPassword === v.confirm, {
    message: "Passwords do not match",
    path: ["confirm"],
  });
type PasswordValues = z.infer<typeof passwordSchema>;

const ChangePasswordCard: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { register, handleSubmit, reset, formState: { errors } } = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
  });

  const mutation = useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      authApi.changePassword(input),
    onSuccess: async () => {
      toast.success("Password changed — please sign in again.");
      reset();
      await logout();
      navigate("/login", { replace: true });
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const onSubmit = handleSubmit((values) =>
    mutation.mutate({ currentPassword: values.currentPassword, newPassword: values.newPassword }),
  );

  return (
    <Card title="Change Password" icon={<Lock size={20} className="text-amber-400" />}>
      <form onSubmit={onSubmit} className="space-y-4">
        <TextField label="Current Password" type="password" error={errors.currentPassword?.message} {...register("currentPassword")} />
        <TextField label="New Password" type="password" error={errors.newPassword?.message} {...register("newPassword")} />
        <TextField label="Confirm New Password" type="password" error={errors.confirm?.message} {...register("confirm")} />
        <Button type="submit" loading={mutation.isPending}>Update password</Button>
      </form>
    </Card>
  );
};

// ───────────────────────── Client profile ─────────────────────────

const ClientProfileCard: React.FC = () => {
  const { data, isLoading, isError, error, refetch } = useMyClientProfile();
  const update = useUpdateMyProfile();
  const { register, handleSubmit, reset } = useForm({
    values: {
      companyName: data?.companyName ?? "",
      contactPerson: data?.contactPerson ?? "",
      phone: data?.phone ?? "",
      address: data?.address ?? "",
      city: data?.city ?? "",
      taxNumber: data?.taxNumber ?? "",
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await update.mutateAsync({
        companyName: values.companyName,
        contactPerson: values.contactPerson || undefined,
        phone: values.phone || undefined,
        address: values.address || undefined,
        city: values.city || undefined,
        taxNumber: values.taxNumber || undefined,
      });
      toast.success("Profile updated");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  });

  return (
    <Card title="Company Profile" icon={<Building2 size={20} className="text-emerald-400" />}>
      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState error={error} onRetry={refetch} />
      ) : (
        <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField label="Company" {...register("companyName")} />
          <TextField label="Contact Person" {...register("contactPerson")} />
          <TextField label="Phone" {...register("phone")} />
          <TextField label="City" {...register("city")} />
          <TextField label="Tax Number" {...register("taxNumber")} />
          <TextField label="Address" {...register("address")} />
          <div className="sm:col-span-2 flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => reset()}>Reset</Button>
            <Button type="submit" loading={update.isPending}>Save profile</Button>
          </div>
        </form>
      )}
    </Card>
  );
};

// ───────────────────────── Users admin ─────────────────────────

const UsersAdminSection: React.FC = () => {
  const { data, isLoading, isError, error, refetch } = useUsers({ limit: 50 });
  const setActive = useSetUserActive();
  const resetPw = useResetUserPassword();
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState<User | null>(null);

  const toggle = async (u: User) => {
    try {
      await setActive.mutateAsync({ id: u.id, isActive: !u.isActive });
      toast.success(`User ${u.isActive ? "deactivated" : "activated"}`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <Card
      title="User Management"
      icon={<ShieldCheck size={20} className="text-violet-400" />}
      action={<Button icon={<UserPlus size={16} />} onClick={() => setCreating(true)}>Add User</Button>}
    >
      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState error={error} onRetry={refetch} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700 text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data?.items.map((u) => (
                <tr key={u.id} className="border-b border-slate-800 last:border-0">
                  <td className="px-3 py-2 font-medium text-slate-100">{u.fullName}</td>
                  <td className="px-3 py-2 text-slate-300">{u.email}</td>
                  <td className="px-3 py-2 text-slate-300">{humanizeEnum(u.role)}</td>
                  <td className="px-3 py-2"><ActiveBadge active={u.isActive} /></td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1">
                      {u.role !== Role.CLIENT && (
                        <button onClick={() => setResetting(u)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-700 hover:text-slate-100" title="Reset password">
                          <KeyRound size={15} />
                        </button>
                      )}
                      <Button variant={u.isActive ? "ghost" : "secondary"} onClick={() => toggle(u)} className="px-3 py-1 text-xs">
                        {u.isActive ? "Deactivate" : "Activate"}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <CreateUserModal onClose={() => setCreating(false)} />}
      {resetting && (
        <ResetPasswordModal
          user={resetting}
          loading={resetPw.isPending}
          onSubmit={async (pw) => {
            try {
              await resetPw.mutateAsync({ id: resetting.id, newPassword: pw });
              toast.success("Password reset");
              setResetting(null);
            } catch (err) {
              toast.error(getErrorMessage(err));
            }
          }}
          onClose={() => setResetting(null)}
        />
      )}
    </Card>
  );
};

const userSchema = z.object({
  fullName: z.string().min(2, "Required"),
  email: z.string().email("Valid email required"),
  password: z.string().min(8, "At least 8 characters"),
  phone: z.string().optional(),
  role: z.enum(["ADMIN", "MANAGER", "STORE_KEEPER", "PRODUCTION_SUPERVISOR"]),
});
type UserValues = z.infer<typeof userSchema>;

const CreateUserModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const create = useCreateUser();
  const { register, handleSubmit, formState: { errors } } = useForm<UserValues>({
    resolver: zodResolver(userSchema),
    defaultValues: { role: "MANAGER" },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await create.mutateAsync(values as CreateUserInput);
      toast.success("User created");
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  });

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Add User"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={onSubmit} loading={create.isPending} icon={<Plus size={16} />}>Create</Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <TextField label="Full Name" required error={errors.fullName?.message} {...register("fullName")} />
        <TextField label="Email" type="email" required error={errors.email?.message} {...register("email")} />
        <TextField label="Password" type="password" required error={errors.password?.message} {...register("password")} />
        <TextField label="Phone" {...register("phone")} />
        <SelectField label="Role" {...register("role")}>
          {INTERNAL_ROLES.map((r) => (
            <option key={r} value={r}>{humanizeEnum(r)}</option>
          ))}
        </SelectField>
      </form>
    </Modal>
  );
};

const ResetPasswordModal: React.FC<{
  user: User;
  loading: boolean;
  onSubmit: (pw: string) => void;
  onClose: () => void;
}> = ({ user, loading, onSubmit, onClose }) => {
  const [pw, setPw] = useState("");
  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Reset Password — ${user.fullName}`}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => (pw.length >= 8 ? onSubmit(pw) : toast.error("At least 8 characters"))} loading={loading}>
            Reset
          </Button>
        </>
      }
    >
      <TextField label="New Password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} />
    </Modal>
  );
};
